import { FC, useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { Delete, DescriptionOutlined, SettingsOutlined, CheckCircleOutline } from "@mui/icons-material";
import { toLower, upperFirst } from "lodash";
import { a } from "@react-spring/web";
import { v4 as uuidv4 } from "uuid";

import BillingSessionNav from "../../../common/pos/billing-session-nav/billing-session-nav";
import CustomerDetailSection from "../../../common/pos/customer-detail-section/customer-detail-section";
import CartSection from "../../../common/pos/cart-section/cart-section";
import ManagerCodeModal from "../../../common/manager-code-modal/ManagerCodeModal";
import CustomMarquee from "../../../common/pos/marquee/marquee";
import OrderDetailSection from "../../../common/pos/order-detail-section/order-detail-section";
import CartQtyHandlerSection from "../../../common/pos/cart-qty-handler-section/cart-qty-handler-section";
import { Colors } from "../../../../configs";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
  addSession,
  newBillAction,
  removeCartItem,
} from "../../../../redux/actions/cartAction";
import useThermalPrinter from "../../../../hooks/thermalPrinter";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PosSliceAction } from "../../../../redux/slice/posSlice";
import { Service } from "../../../../typings/enum/Service";
import PaymentModal from "../../payment-modal/payment-modal";
import useCart from "../../../../hooks/useCart";
import { getPricing, getUnavailableMenuItems } from "../../../../redux/actions/pricingAction";
import { getPriceListsAction } from "../../../../redux/actions/priceListsAction";
import { PaymentModalContents } from "../../../../typings/enum/paymentModalContents";
import { monthNames } from "../../../../data";
import BillNotesModal from "../../notes-modal/billNotesModal";
import { getUserNotesByIdAction } from "../../../../redux/actions/customerAction";
import { useUserNote } from "../../../../providers/UserNoteProvider";
import { NoteOccasion } from "../../../../typings/enum/noteOccasions";
import { CustomButton, PosButton } from "../../../atoms";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { AssignPinTagModal } from "../../../common/pos/assign-pin-tag-modal/assign-pin-tag-modal";
import { CartSliceAction } from "../../../../redux/slice/cartSlice";
import { BillPickupStatus } from "../../../../typings/enum/paymentTypes";
import { DiscountSliceAction } from "../../../../redux/slice/discountSlice";
import { ICustomerBill } from "../../../../typings";
import { ICartSession } from "../../../../typings/interfaces/pos/cart-session";
import ConfirmDialog from "../../../atoms/confirm-box/confirm-box";
import WarningDialog from "../../../atoms/warning-dialog/WarningDialog";
import {
  TicketMaxItemsDialog,
  TicketNoItemsDialog,
} from "../../../common/pos/ticket-validation-dialogs/ticket-validation-dialogs";
import { IBillItemLimit, countBillPieces, resolveBillItemLimit } from "../../../../util/bill-item-limit";
import { firstSessionNeedingPinTag } from "../../../../util/pin-tag-completeness";

export interface ICartProps {
  editingBillId?: string;
}

// Editing a ticket: its pin tags are full (3.m/3.n)
const pinTagsFullMessage = ({ tags, itemsPerTag }: { tags: number; itemsPerTag: number }) =>
  `This ticket has ${tags} pin tag${tags === 1 ? "" : "s"}, and each covers ${itemsPerTag} item${
    itemsPerTag === 1 ? "" : "s"
  }, so it can hold ${tags * itemsPerTag}. No more pin tags can be added when editing. Remove items, or put new items on a new ticket.`;

const Cart: FC<ICartProps> = ({ editingBillId }) => {
  const [openCheckInMdal, setOpenCheckInMdal] = useState(false);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [openAssignPinTagModal, setOpenAssignPinTagModal] = useState(false);
  const [pinTagSessionId, setPinTagSessionId] = useState<string | null>(null);
  const [openClearCartDialog, setOpenClearCartDialog] = useState(false);

  const [openMixedServiceWarning, setOpenMixedServiceWarning] = useState(false);
  const [mixedServiceWarningMessage, setMixedServiceWarningMessage] = useState("");
  const [openNoItemsDialog, setOpenNoItemsDialog] = useState(false);
  // Over-limit sessions found at check-in; adds/qty changes use the slice's flag
  const [checkInLimitBreach, setCheckInLimitBreach] = useState<IBillItemLimit | null>(null);
  const [openManagerCodeModal, setOpenManagerCodeModal] = useState(false);

  const [openActionTileModal, setOpenActionTileModal] =
    useState<boolean>(false);
  const [isCheckInSuccessful, setIsCheckInSuccessful] = useState<boolean>(true);

  // Long press functionality
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const longPressDelay = 1000; // 1 second

  const {
    cart,
    selectedCartItem,
    selectedSession,
    selectedServiceType,
    cartMode,
    billByIdLoading,
    billById,
    billItemIdList,
    billItemLimitExceeded,
  } = useAppSelector((state) => state.cart);
  const categories = useAppSelector((state) => state.pricing.categoryResponse?.body);
  const defaultMaxItemsPerBill = useAppSelector(
    (state) => state.merchant.merchantListResponse?.body?.[0]?.defaultMaxItemsPerBill
  );
  const { cartTotal, totalDiscountAmount } = useCart();
  const { customer, customerNotes, customerLoading } = useAppSelector(
    (state) => state.customer
  );

  const { activeServiceTab } = useAppSelector((state) => state.pos);
  const { priceLists } = useAppSelector((state) => state.priceLists);
  const { user } = useAppSelector((state) => state.auth);
  const { configure, isConfigured } = useThermalPrinter();
  const { showNote } = useUserNote();
  const snackbar = useSnackbarContext();

  const navigate = useNavigate();
  // const [marqueeText, setMarqueeText] = useState("");
  const dispatch = useAppDispatch();
  const params = useParams();
  const location = useLocation();
  const billId = params.billId;

  const customerId = params.id;

  // Defense-in-depth mirroring CartRow's own selection guard - an
  // already-picked-up or already-paid-for item (Edit Invoice, against a
  // saved bill) should never actually reach the Delete action even if
  // `selectedCartItem` somehow ends up pointing at one.
  const isSelectedItemLocked = useMemo(() => {
    if (!selectedCartItem) return false;
    const entry = billItemIdList.find((b) => b.billId === billById?.id);
    return (
      entry?.billItemIdList.some(
        (item) =>
          item.hashId === selectedCartItem.itemId &&
          (item.pickupStatus === BillPickupStatus.PICKED_UP || item.isPaid === true)
      ) ?? false
    );
  }, [billItemIdList, billById?.id, selectedCartItem]);

  const handleOnClickDelete = () => {
    if (isSelectedItemLocked) return;
    dispatch(removeCartItem());
  };

  // Long press handlers for clear all cart items
  const handleMouseDown = () => {
    longPressTimer.current = setTimeout(() => {
      setOpenClearCartDialog(true);
    }, longPressDelay);
  };

  const handleMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Clear all cart items
  const handleClearAllCartItems = () => {
    dispatch(CartSliceAction.clearSession());
    dispatch(DiscountSliceAction.resetDiscount());

    // Create a new empty session so users can continue adding items
    const newSession: ICartSession = {
      sessionId: uuidv4(),
      cartItems: [],
      customerId: customerId ? parseInt(customerId) : 0,
      pinTag: {
        color: null,
        tags: [],
        date: null,
      },
    };

    dispatch(addSession(newSession));
    dispatch(CartSliceAction.setSelectedSession(newSession));

    setOpenClearCartDialog(false);
    snackbar.showSnackbar("All cart items have been cleared", "success");
  };

  // Cancel clear cart dialog
  const handleCancelClearCart = () => {
    setOpenClearCartDialog(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchNotes = async () => {
      if (customerId) {
        const res: any = await dispatch(
          getUserNotesByIdAction(customerId as string)
        );
        showNote(NoteOccasion.ON_CHECKIN, res.payload);
      }
    };

    fetchNotes();
  }, [customerId, dispatch]);

  const loadNotes = async () => {
    if (customerId) {
      await dispatch(getUserNotesByIdAction(customerId as string)).then(
        (res: any) => {
          showNote(NoteOccasion.ON_CHECKIN, res.payload);
        }
      );
    }
  };

  const changeCartMode = (mode: "check-in" | "pickup") => {
    if (cartMode !== mode) {
      dispatch(CartSliceAction.setCartMode(mode));
    }
    dispatch(CartSliceAction.clearSession());
    dispatch(DiscountSliceAction.resetDiscount());
  };

  useEffect(() => {
    changeCartMode("check-in");
  }, [cartMode, location.pathname]);

  // Which price list to price with: an edited bill keeps the list it was
  // created with (older bills, with none recorded, use the default); a new
  // check-in uses the customer's list. Undefined while the bill loads.
  const pricingScope = useMemo(() => {
    if (billId) {
      return billById ? { priceListId: billById.priceListId ?? null } : undefined;
    }
    return { customerId: customerId ?? null };
  }, [billId, billById?.id, billById?.priceListId, customerId]);
  const pricingScopeKey = JSON.stringify(pricingScope ?? null);

  useEffect(() => {
    if (selectedServiceType?.id && pricingScope)
      dispatch(getPricing({ id: selectedServiceType?.id, ...pricingScope }));
  }, [selectedServiceType?.id, pricingScopeKey]);

  useEffect(() => {
    if (pricingScope) dispatch(getUnavailableMenuItems(pricingScope));
  }, [pricingScopeKey]);

  useEffect(() => {
    if (priceLists.length === 0) dispatch(getPriceListsAction());
  }, []);

  // Named only when it isn't the default, so cashiers notice special pricing
  const activePriceListId = billId ? billById?.priceListId : customer?.priceListId;
  const activePriceList = priceLists.find((list) => list.id === activePriceListId);
  const priceListNotice =
    activePriceList && !activePriceList.isDefault ? `Price list: ${activePriceList.name}` : "";

  const handleOnClickCustomization = () => {
    if (activeServiceTab === Service.CUSTOMIZATION) {
      dispatch(CartSliceAction.setSelectedCartItem(null));
    }
    dispatch(
      PosSliceAction.setActiveServiceTab(
        activeServiceTab === Service.GARMENT_WASH_AND_CLEAN
          ? Service.CUSTOMIZATION
          : Service.GARMENT_WASH_AND_CLEAN
      )
    );
  };

  const handleOnCloseCheckInModal = () => {
    setOpenCheckInMdal(false);
  };
  const handleOnCloseNotesModal = () => {
    setOpenNotesModal(false);
  };

  const handleOnCheckIn = () => {
    let isEmptySession = true;

    for (const session of cart) {
      const finalizedItems = session.cartItems?.filter((item) => item.finalized) || [];

      if (finalizedItems.length > 0) {
        isEmptySession = false;

        let hasPressOnly = false;
        let hasCleanOnly = false;
        let hasRedo = false;
        let hasStandard = false;

        let hasNonRedo = false;

        // Redo and Press/Clean Only are checked separately: a redo item can
        // also be Press Only or Clean Only, so an all-redo bill must still
        // not mix the two.
        finalizedItems.forEach(item => {
          if (item.redoDetails) {
            hasRedo = true;
          } else {
            hasNonRedo = true;
          }
          if (item.pressOnly) {
            hasPressOnly = true;
          } else if (item.cleanOnly) {
            hasCleanOnly = true;
          } else {
            hasStandard = true;
          }
        });

        // Validation Logic
        if (hasPressOnly && (hasCleanOnly || hasStandard)) {
          setMixedServiceWarningMessage("Press-only garments cannot be listed with any other service options.\n\nPlease remove all non press-only items to proceed!");
          setOpenMixedServiceWarning(true);
          return;
        }

        if (hasCleanOnly && (hasPressOnly || hasStandard)) {
          setMixedServiceWarningMessage("Clean-only garments cannot be listed with any other service options.\n\nPlease remove all non clean-only items to proceed!");
          setOpenMixedServiceWarning(true);
          return;
        }

        if (hasRedo && hasNonRedo) {
          setMixedServiceWarningMessage("Redo garments cannot be listed with any other service options.\n\nPlease remove all non redo items to proceed!");
          setOpenMixedServiceWarning(true);
          return;
        }
      }
    }


    // A new ticket can't be checked in over its limit (e.g. a draft restored
    // after the limit was lowered). Edits are exempt so an existing bill that
    // is already over can still be saved.
    const overLimit = billId
      ? undefined
      : cart
          .map((session) => ({ session, limit: resolveBillItemLimit(session, categories, defaultMaxItemsPerBill) }))
          .find(({ session, limit }) => limit && countBillPieces(session) > limit.maxItems);

    if (isEmptySession) {
      setOpenNoItemsDialog(true);
    } else if (overLimit?.limit) {
      dispatch(CartSliceAction.setSelectedSession(overLimit.session));
      setCheckInLimitBreach(overLimit.limit);
    } else {
      let isRedoBill = false;
      cart.forEach((session) => {
        if (session.cartItems?.some((item) => item.redoDetails)) {
          isRedoBill = true;
        }
      });

      if (isRedoBill) {
        setOpenManagerCodeModal(true);
      } else {
        proceedToCheckIn();
      }
    }
  };

  // The ticket the Assign Pintag modal is for (defaults to the last one)
  const pinTagSession =
    cart.find((session) => session.sessionId === pinTagSessionId) ??
    cart[cart.length - 1];

  const proceedToCheckIn = () => {
    setOpenManagerCodeModal(false);
    // Every ticket needs a complete pin tag, not just the last one: items
    // added to an earlier ticket after its tag was picked leave a blank slot
    const target = firstSessionNeedingPinTag(cart);
    if (target) {
      setPinTagSessionId(target.sessionId);
      setOpenAssignPinTagModal(true);
    } else {
      setOpenCheckInMdal(true);
    }
  };
  let isEmptySession = true;
  cart.forEach((session) => {
    if (session.cartItems?.filter((item) => item.finalized).length > 0) {
      isEmptySession = false;
    } else {
      isEmptySession = true;
      return;
    }
  });

  const handleOpenModal = () => {
    setOpenActionTileModal(true);
  };

  return (
    <Box
      sx={{
        flex: 4,
        display: "flex",
        flexDirection: "column",
        gap: "0.57rem",
        height: "100%",
        maxHeight: "100% !important",
        width: "100%",
        maxWidth: "100%",
        justifyContent: "space-between",
      }}
    >
      <AssignPinTagModal
        key={pinTagSession?.sessionId}
        isOpen={openAssignPinTagModal}
        onClose={() => setOpenAssignPinTagModal(false)}
        serviceTypeId={
          pinTagSession?.cartItems[0]?.serviceType?.id ?? 0
        }
        itemsCount={
          pinTagSession?.cartItems.reduce(
            (acc, item) => (item.finalized ? acc + item.qty : acc),
            0
          ) ?? 0
        }
        sessionId={pinTagSession?.sessionId ?? ""}
        initialColor={pinTagSession?.pinTag?.color ?? undefined}
        initialDate={pinTagSession?.pinTag?.date ?? undefined}
        initialTags={pinTagSession?.pinTag?.tags ?? undefined}
        pinTagDraftSession={{
          sessionId: pinTagSession?.sessionId ?? "",
          customerId: pinTagSession?.customerId ?? 0,
          cartItems: pinTagSession?.cartItems ?? [],
          pinTag: { color: null, tags: [], date: null },
        }}
        onSubmit={async (updatedSession) => {
          await dispatch(addSession(updatedSession));
          dispatch(CartSliceAction.setSelectedSession(updatedSession));
          dispatch(
            CartSliceAction.setSelectedServiceType(
              updatedSession.cartItems[0]?.serviceType || null
            )
          );
          // Move on to the next ticket still missing a tag, if any
          const next = firstSessionNeedingPinTag(
            cart.map((session) =>
              session.sessionId === updatedSession.sessionId
                ? updatedSession
                : session
            )
          );
          if (next) {
            setPinTagSessionId(next.sessionId);
            return;
          }
          setOpenCheckInMdal(true);
          setOpenAssignPinTagModal(false);
        }}
      />

      <PaymentModal
        total={cartTotal}
        discount={totalDiscountAmount}
        open={openCheckInMdal}
        onClose={handleOnCloseCheckInModal}
        // onCheckout={handleOnCheckout}
        content={PaymentModalContents.SUMMARY}
      />

      {/* Clear Cart Confirmation Dialog */}
      <ConfirmDialog
        open={openClearCartDialog}
        title="Clear Cart"
        message="Do you really want to clear all cart items?"
        positiveButtonText="Yes"
        negativeButtonText="No"
        onConfirm={handleClearAllCartItems}
        onCancel={handleCancelClearCart}
      />

      {/* Mixed Service Warning Dialog */}
      <WarningDialog
        open={openMixedServiceWarning}
        onClose={() => setOpenMixedServiceWarning(false)}
        onOk={() => setOpenMixedServiceWarning(false)}
        message={mixedServiceWarningMessage}
      />

      <TicketNoItemsDialog
        open={openNoItemsDialog}
        ticketNumber={billId ? billById?.id : undefined}
        customerName={customer ? `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim() : undefined}
        onOk={() => setOpenNoItemsDialog(false)}
      />

      <TicketMaxItemsDialog
        open={!!(billItemLimitExceeded || checkInLimitBreach)}
        ticketNumber={billId ? billById?.id : undefined}
        customerName={customer ? `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim() : undefined}
        maxItems={(billItemLimitExceeded ?? checkInLimitBreach)?.maxItems ?? 0}
        serviceName={(billItemLimitExceeded ?? checkInLimitBreach)?.serviceName}
        message={
          billItemLimitExceeded?.pinTags
            ? pinTagsFullMessage(billItemLimitExceeded.pinTags)
            : checkInLimitBreach && !billItemLimitExceeded
            ? `A ${checkInLimitBreach.serviceName ? `${checkInLimitBreach.serviceName} ` : ""}ticket can hold a maximum of ${checkInLimitBreach.maxItems} piece${checkInLimitBreach.maxItems === 1 ? "" : "s"}, and this ticket has ${countBillPieces(selectedSession)}. Please remove items or move them to a new ticket before checking in.`
            : undefined
        }
        onOk={() => {
          dispatch(CartSliceAction.clearBillItemLimitExceeded());
          setCheckInLimitBreach(null);
        }}
        // Editing a bill can't add tickets
        onNewTicket={
          billId
            ? undefined
            : () => {
                dispatch(CartSliceAction.clearBillItemLimitExceeded());
                setCheckInLimitBreach(null);
                dispatch(CartSliceAction.requestNewSession());
              }
        }
      />

      <ManagerCodeModal
        open={openManagerCodeModal}
        onClose={() => setOpenManagerCodeModal(false)}
        title="Manager Code"
        task="access the Redo Check In"
        onDone={proceedToCheckIn}
        actionType="REDO_CHECK_IN"
        requestedByUserId={user?.id}
        billId={billById?.id}
      />

      <BillingSessionNav editingBillId={editingBillId} />
      <CustomerDetailSection
        showAnalysisButton={!!!editingBillId}
        showEditButton={!!!editingBillId}
        showHistoryButton={!!!editingBillId}
      />

      <CartSection onSelect={() => { }} />

      <CustomMarquee
        text={(() => {
          const notesText =
            customerNotes == null || customerNotes.length === 0
              ? ""
              : customerNotes
                .filter((note) =>
                  note.noteShowOccasion?.includes(NoteOccasion.ON_CHECKIN)
                )
                .map((note) => note.note)
                .join(", ");

          const alterationText =
            customer?.automaticAlteration
              ? `Automatic Alteration: ON | Allowed Amount: $${customer.allowedAmount ?? "0"}`
              : "";

          return [priceListNotice, notesText, alterationText].filter(Boolean).join("   |   ");
        })()}
      />
      {/* <Marquee>{marqueeText}</Marquee> */}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1.07142857rem",
          flexShrink: 0,
        }}
      >
        <OrderDetailSection />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.71rem",
            maxHeight: "16.07142857142857rem",
            flex: 1,
          }}
        >
          <CartQtyHandlerSection />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "0.71rem",
              flexWrap: "nowrap",
              width: "100%",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <IconButton
              sx={{
                background: Colors.MAIN_GRAY,
                height: "3.714285714285714rem",
                width: "3.571428571428571rem",
                aspectRatio: "1/1",
                flexShrink: 0,
                borderRadius: "1rem",
                "&:hover": { background: `${Colors.MAIN_GRAY} !important` },
                "&:disabled": { background: `${Colors.MAIN_GRAY} !important` },
              }}
              disabled={selectedCartItem ? isSelectedItemLocked : true}
              onClick={handleOnClickDelete}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              name="delete-cart-item"
            >
              <Delete
                sx={{
                  color: Colors.WHITE,
                  fontSize: "1.78571429rem",
                }}
              />
            </IconButton>
            <BillNotesModal
              open={openNotesModal}
              onClose={handleOnCloseNotesModal}
            />
            <PosButton
              test-name="notes-button"
              variantType="secondary"
              sx={{
                flex: 1,
                width: {
                  xs: "7.314285714285714rem",
                  sm: "9.314285714285714rem",
                },
                minWidth: {
                  xs: "7.314285714285714rem",
                  sm: "9.314285714285714rem",
                },
                gap: "0.4rem",
              }}
              onClick={() => setOpenNotesModal(true)}
              disabled={isEmptySession}
            >
              <DescriptionOutlined sx={{ fontSize: "1.6rem" }} />
              Notes
            </PosButton>
            <PosButton
              variantType="secondary"
              disabled={
                selectedCartItem?.finalized === true
                  ? isSelectedItemLocked
                  : true
              }
              sx={{
                flex: 1,
                width: "13.78571428571429rem",
                minWidth: "13.78571428571429rem",
                gap: "0.4rem",
              }}
              onClick={handleOnClickCustomization}
            >
              <SettingsOutlined sx={{ fontSize: "1.6rem" }} />
              {activeServiceTab === Service.GARMENT_WASH_AND_CLEAN
                ? "Customization"
                : "Add Items"}
            </PosButton>
          </Box>

          <PosButton
            onClick={handleOnCheckIn}
            name="check-in"
            loading={billByIdLoading || customerLoading}
            sx={{ gap: "0.6rem" }}
          >
            <CheckCircleOutline sx={{ fontSize: "2.28rem" }} />
            {billId ? "Update Bill" : "Check In"}
          </PosButton>

          {/* <CustomButton
            onClick={configure}
            disabled={isConfigured}
            sx={{ background: Colors.ACCENT_GRAY, height: "" }} //a minimum height is temporary assigned for the button
          >
            Config Printer
          </CustomButton> */}
        </Box>
      </Box>
    </Box>
  );
};

export default Cart;
